export const REVIEW_GATE_STATUS = {
    IN_PROGRESS: "in_progress",
    READY_FOR_QA: "ready_for_qa",
    QA_PASSED: "qa_passed",
    REOPENED: "reopened"
};

// The three manually-selectable statuses. Excludes "Reopened" (system-set) and is also
// consumed by the building Executive Summary QA controls, so keep it to these three.
export const ALL_REVIEW_GATE_OPTIONS = [
    { label: "In Progress", value: REVIEW_GATE_STATUS.IN_PROGRESS },
    { label: "Ready for QA", value: REVIEW_GATE_STATUS.READY_FOR_QA },
    { label: "QA Passed", value: REVIEW_GATE_STATUS.QA_PASSED }
];

// Recommendation QA-gate dropdown order. Includes "Reopened" (rendered disabled) so a
// reopened recommendation reads correctly.
export const REVIEW_GATE_DISPLAY_OPTIONS = [
    { label: "In Progress", value: REVIEW_GATE_STATUS.IN_PROGRESS },
    { label: "Reopened", value: REVIEW_GATE_STATUS.REOPENED },
    { label: "Ready for QA", value: REVIEW_GATE_STATUS.READY_FOR_QA },
    { label: "QA Passed", value: REVIEW_GATE_STATUS.QA_PASSED }
];

export const REVIEW_GATE_STATUS_LABELS = {
    [REVIEW_GATE_STATUS.IN_PROGRESS]: "In Progress",
    [REVIEW_GATE_STATUS.READY_FOR_QA]: "Ready for QA",
    [REVIEW_GATE_STATUS.QA_PASSED]: "QA Passed",
    [REVIEW_GATE_STATUS.REOPENED]: "Reopened"
};

export const getReviewGateStatus = rec => {
    const status = rec?.qa_qc_status;
    if (REVIEW_GATE_STATUS_LABELS[status]) return status;
    return REVIEW_GATE_STATUS.IN_PROGRESS;
};

export const normalizeImageList = imageResponse => {
    if (Array.isArray(imageResponse)) return imageResponse;
    if (Array.isArray(imageResponse?.images)) return imageResponse.images;
    return [];
};

const getImageCaption = img => (img?.description || img?.caption || "").trim();

export const getReadyForQaCriteria = ({ images, basicDetails, issues = [] }) => {
    const imageList = normalizeImageList(images);
    const displayUnit = basicDetails?.project?.display_unit;
    const missingCaptionCount = imageList.filter(img => !getImageCaption(img)).length;
    const pendingCount = issues.filter(({ status }) => status === "pending").length;

    const criteria = [
        {
            id: "has_images",
            label: "At least one image is present",
            failMessage: "Add at least one image",
            met: imageList.length > 0
        },
        {
            id: "captions",
            label: "All images have captions",
            failMessage:
                imageList.length === 0
                    ? "All images must have captions"
                    : missingCaptionCount === 1
                      ? "Add a caption to 1 image that is missing one"
                      : `Add captions to all images (${missingCaptionCount} missing)`,
            met: imageList.length > 0 && missingCaptionCount === 0
        }
    ];

    if (displayUnit) {
        const costMatch =
            Math.round(basicDetails?.project_total || 0) === Math.round(basicDetails?.total_opinions_of_cost || 0);
        criteria.push({
            id: "cost_match",
            label: "Construction cost matches Opinions of Cost",
            failMessage: "Construction cost must match Opinions of Cost",
            met: costMatch
        });
    }

    criteria.push({
        id: "no_pending_issues",
        label: "No pending QA/QC issues",
        failMessage:
            pendingCount === 1
                ? "1 QA/QC issue is still pending"
                : `${pendingCount} QA/QC issues are still pending`,
        met: pendingCount === 0
    });

    return criteria;
};

export const getQaPassedDisabledReason = (issues = []) => {
    const pending = issues.filter(({ status }) => status === "pending");
    const resolved = issues.filter(({ status }) => status === "resolved");
    const parts = [];

    if (pending.length) {
        parts.push(
            `${pending.length} issue${pending.length === 1 ? "" : "s"} still pending and must be resolved`
        );
    }
    if (resolved.length) {
        parts.push(
            `${resolved.length} issue${resolved.length === 1 ? "" : "s"} resolved but must be approved`
        );
    }

    if (!parts.length) return null;
    return `${parts.join(". ")} before marking as QA/QC Passed.`;
};

export const QA_PASSED_REQUIRES_READY_TIP = "Move this to Ready for QA before marking it as QA/QC Passed.";

export const canMoveToQaPassed = currentStatus => currentStatus === REVIEW_GATE_STATUS.READY_FOR_QA;

export const enrichReviewGateOptions = ({ isQaQcReviewerUser, readyForQaCriteria, issues = [], currentStatus }) => {
    const readyValid = readyForQaCriteria.every(c => c.met);
    const qaPassedBlockedByStatus = currentStatus ? !canMoveToQaPassed(currentStatus) : false;
    const criteriaChecklistHtml = buildCriteriaChecklistHtml(readyForQaCriteria);
    const qaPassedDisabledReason = getQaPassedDisabledReason(issues);
    const base = isQaQcReviewerUser
        ? REVIEW_GATE_DISPLAY_OPTIONS
        : REVIEW_GATE_DISPLAY_OPTIONS.filter(o => o.value !== REVIEW_GATE_STATUS.QA_PASSED);

    return base.map(opt => {
        if (opt.value === REVIEW_GATE_STATUS.REOPENED) {
            // System-set only — visible but never manually selectable.
            return { ...opt, isDisabled: true };
        }
        if (opt.value === REVIEW_GATE_STATUS.READY_FOR_QA) {
            return {
                ...opt,
                isDisabled: !readyValid,
                criteriaTipHtml: criteriaChecklistHtml,
                ...(readyValid ? {} : { disabledTipHtml: criteriaChecklistHtml })
            };
        }
        if (opt.value === REVIEW_GATE_STATUS.QA_PASSED && (!readyValid || qaPassedDisabledReason || qaPassedBlockedByStatus)) {
            const statusTip = qaPassedBlockedByStatus ? QA_PASSED_REQUIRES_READY_TIP : null;
            return {
                ...opt,
                isDisabled: true,
                ...(qaPassedDisabledReason
                    ? { disabledTip: qaPassedDisabledReason }
                    : statusTip
                    ? { disabledTip: statusTip }
                    : { disabledTipHtml: criteriaChecklistHtml })
            };
        }
        return opt;
    });
};

export const REVIEW_GATE_STATUS_CLASS = {
    [REVIEW_GATE_STATUS.IN_PROGRESS]: "pending-orange",
    [REVIEW_GATE_STATUS.READY_FOR_QA]: "gate-ready-blue",
    [REVIEW_GATE_STATUS.QA_PASSED]: "res-green",
    [REVIEW_GATE_STATUS.REOPENED]: "gate-reopened"
};

export const REVIEW_GATE_OPTION_CLASS = {
    [REVIEW_GATE_STATUS.IN_PROGRESS]: "review-gate-option--in-progress",
    [REVIEW_GATE_STATUS.READY_FOR_QA]: "review-gate-option--ready",
    [REVIEW_GATE_STATUS.QA_PASSED]: "review-gate-option--passed",
    [REVIEW_GATE_STATUS.REOPENED]: "review-gate-option--reopened"
};

export const REVIEW_GATE_OPTION_TIP_IDS = {
    [REVIEW_GATE_STATUS.READY_FOR_QA]: "review-gate-opt-ready_for_qa",
    [REVIEW_GATE_STATUS.QA_PASSED]: "review-gate-opt-qa_passed"
};

export const getReviewGateConfirmCopy = (nextStatus, currentStatus) => {
    switch (nextStatus) {
        case REVIEW_GATE_STATUS.IN_PROGRESS:
            if (currentStatus === REVIEW_GATE_STATUS.QA_PASSED) {
                return {
                    heading: "Do you want to mark this as not QA/QC Passed?",
                    message: "Once marked this recommendation will be unlocked."
                };
            }
            return {
                heading: "Move back to In Progress?",
                message: "This recommendation will no longer be marked as ready for QA/QC."
            };
        default:
            return { heading: "", message: "" };
    }
};

export const buildCriteriaChecklistHtml = (criteria, { title = "Requirements for Ready for QA" } = {}) => {
    const items = criteria
        .map(
            c =>
                `<div class="qa-gate-criteria__item ${c.met ? "is-met" : "is-unmet"}">` +
                `<span class="qa-gate-criteria__icon" aria-hidden="true">${c.met ? "&#10003;" : "&#10007;"}</span>` +
                `<span class="qa-gate-criteria__text">${c.met ? c.label : c.failMessage || c.label}</span>` +
                `</div>`
        )
        .join("");

    return `<div class="qa-gate-criteria"><div class="qa-gate-criteria__title">${title}</div><div class="qa-gate-criteria__items">${items}</div></div>`;
};

export const getReviewGateApiPayload = status => {
    if (!REVIEW_GATE_STATUS_LABELS[status]) return null;
    return { qa_qc_status: status };
};

